import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from '@/design-system/components/toast'
import { ApiError, isMaintenance, maintenanceOf } from '@/lib/api/errors'
import { MODULES_QUERY, isModuleRefusal } from '@/features/modules/refusal'

/**
 * Server state lives here, not in component state or a global store.
 *
 * The defaults favour fewer requests: data is treated as fresh for 30s, and
 * focusing the window does not trigger a refetch storm across every mounted
 * query. Screens that need live data opt in with refetchInterval.
 */
/**
 * A request refused because its module is unavailable to the company (P09,
 * 403 `module_unavailable`): the manifest is out of date — refresh it and
 * every guide, so the navigation and the screen's gate catch up.
 */
function refreshModules(error: unknown): void {
  if (isModuleRefusal(error)) void queryClient.invalidateQueries({ queryKey: MODULES_QUERY }, { cancelRefetch: false })
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: refreshModules }),
  // A change refused because the platform is in read-only maintenance
  // (P16): say so, in the platform's own words, wherever it happened.
  mutationCache: new MutationCache({
    onError: (error) => {
      const notice = maintenanceOf(error)
      if (notice !== null) toast(notice.message, 'settings')
      refreshModules(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 4xx will not fix itself on retry — 401, 403, 404 and 422 are
        // answers, not outages. Only retry what might be transient.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false
        }
        // Maintenance answers until the window ends; the page watches /api/status instead.
        if (isMaintenance(error)) {
          return false
        }

        return failureCount < 2
      },
    },
    mutations: {
      // Mutations are not idempotent in general; never replay one silently.
      retry: false,
    },
  },
})
